import { useMemo, useState } from "react";
import Modal from "../ui/Modal";
import Button from "../ui/Button";
import { useServices } from "../../context/ServicesContext";
import { useUiPreferences } from "../../context/UiPreferencesContext";
import { normalizeServiceScope, TREATMENT_SCOPES } from "../../utils/treatmentScope";
import { rgbaFromHex } from "../../utils/colorUtils";

const buildCategoryList = (services = []) => {
  const map = new Map();
  services.forEach((service) => {
    const name = (service.category || "Altele").trim() || "Altele";
    if (!map.has(name)) {
      map.set(name, {
        name,
        order: typeof service.categoryOrder === "number" ? service.categoryOrder : 0,
        services: [],
      });
    }
    map.get(name).services.push(service);
  });

  return Array.from(map.values())
    .map((entry) => ({
      ...entry,
      services: entry.services.slice().sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order;
        return a.name.localeCompare(b.name);
      }),
    }))
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.name.localeCompare(b.name);
    });
};

const emptyForm = {
  id: null,
  name: "",
  category: "",
  basePrice: 0,
  defaultDiscount: 0,
  active: true,
  priceA: "",
  priceB: "",
  priceC: "",
  commissionTag: "",
  scope: "tooth",
};

export default function ServicesCatalogModal({ open, onClose }) {
  const {
    services,
    loading,
    error,
    createService,
    updateService,
    deleteService,
    reorderServices,
  } = useServices();
  const { categoryColors, setCategoryColor } = useUiPreferences();

  const [showInactive, setShowInactive] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [formState, setFormState] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [dragCategoryIndex, setDragCategoryIndex] = useState(null);
  const [dragService, setDragService] = useState(null);

  const visibleServices = useMemo(() => {
    if (showInactive) return services;
    return (services || []).filter((service) => service.active !== false);
  }, [services, showInactive]);

  const categories = useMemo(() => buildCategoryList(visibleServices), [visibleServices]);

  const toggleCategory = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const openCreateForm = (categoryName = "") => {
    setFormState({
      ...emptyForm,
      category: categoryName || "",
    });
  };

  const openEditForm = (service) => {
    const priceByGroup = service?.meta?.priceByGroup || {};
    const normalizedScope = normalizeServiceScope(service?.scope);
    setFormState({
      id: service.id,
      name: service.name || "",
      category: service.category || "",
      basePrice: Number(service.basePrice || 0),
      defaultDiscount: Number(service.defaultDiscount || 0),
      active: service.active !== false,
      priceA: typeof priceByGroup.A === "number" ? String(priceByGroup.A) : "",
      priceB: typeof priceByGroup.B === "number" ? String(priceByGroup.B) : "",
      priceC: typeof priceByGroup.C === "number" ? String(priceByGroup.C) : "",
      commissionTag: service?.meta?.commissionTag || "",
      scope: normalizedScope === TREATMENT_SCOPES.ANY ? TREATMENT_SCOPES.FULL : normalizedScope,
    });
  };

  const handleFormChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formState.name.trim()) {
      alert("Completează numele serviciului.");
      return;
    }
    const categoryName = formState.category.trim();
    if (!categoryName) {
      alert("Completează categoria.");
      return;
    }

    const priceA = formState.priceA !== "" ? Number(formState.priceA) : null;
    const priceB = formState.priceB !== "" ? Number(formState.priceB) : null;
    const priceC = formState.priceC !== "" ? Number(formState.priceC) : null;
    const priceByGroup = {};
    if (Number.isFinite(priceA)) priceByGroup.A = priceA;
    if (Number.isFinite(priceB)) priceByGroup.B = priceB;
    if (Number.isFinite(priceC)) priceByGroup.C = priceC;

    const existingCategory = categories.find((cat) => cat.name === categoryName);
    const categoryOrder = existingCategory
      ? existingCategory.order
      : categories.length;

    const basePrice = Number(formState.basePrice || 0);
    const defaultDiscount = Number(formState.defaultDiscount || 0);

    const payloadMeta = {
      ...(Object.keys(priceByGroup).length ? { priceByGroup } : {}),
      ...(formState.commissionTag ? { commissionTag: formState.commissionTag } : {}),
    };

    const payload = {
      name: formState.name.trim(),
      category: categoryName,
      categoryOrder,
      basePrice,
      defaultDiscount,
      active: Boolean(formState.active),
      scope: formState.scope || "tooth",
      meta: Object.keys(payloadMeta).length ? payloadMeta : null,
    };

    if (!formState.id) {
      payload.order = existingCategory ? existingCategory.services.length : 0;
    }

    try {
      setSaving(true);
      if (formState.id) {
        await updateService({ id: formState.id, ...payload });
      } else {
        await createService(payload);
      }
      setFormState(emptyForm);
    } catch (err) {
      alert(err.message || "Nu s-a putut salva serviciul");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!serviceId) return;
    if (!window.confirm("Sigur ștergi serviciul?") ) return;
    try {
      await deleteService(serviceId);
    } catch (err) {
      alert(err.message || "Nu s-a putut șterge serviciul");
    }
  };

  const handleCategoryDrop = async (targetIndex) => {
    if (dragCategoryIndex === null || dragCategoryIndex === targetIndex) {
      setDragCategoryIndex(null);
      return;
    }
    const next = categories.slice();
    const [moved] = next.splice(dragCategoryIndex, 1);
    next.splice(targetIndex, 0, moved);

    const updates = [];
    next.forEach((cat, idx) => {
      cat.services.forEach((srv) => {
        updates.push({ id: srv.id, categoryOrder: idx });
      });
    });

    try {
      await reorderServices(updates);
    } catch (err) {
      alert(err.message || "Nu am putut rearanja categoriile");
    } finally {
      setDragCategoryIndex(null);
    }
  };

  const handleServiceDrop = async (categoryName, targetIndex) => {
    if (!dragService || dragService.category !== categoryName) {
      setDragService(null);
      return;
    }
    const category = categories.find((cat) => cat.name === categoryName);
    if (!category) return;

    const nextServices = category.services.slice();
    const [moved] = nextServices.splice(dragService.index, 1);
    nextServices.splice(targetIndex, 0, moved);

    const updates = nextServices.map((srv, idx) => ({ id: srv.id, order: idx }));

    try {
      await reorderServices(updates);
    } catch (err) {
      alert(err.message || "Nu am putut rearanja serviciile");
    } finally {
      setDragService(null);
    }
  };

  return (
    <Modal
      title="Catalog servicii"
      onClose={onClose}
      size="xl"
      contentClassName="max-w-6xl"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-gray-600">
            Gestionează categoriile și serviciile clinicii. Drag & drop pentru reordonare.
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs sm:text-sm flex items-center gap-2">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Arată servicii inactive
            </label>
            <Button size="sm" variant="secondary" onClick={() => openCreateForm()}>
              + Adaugă serviciu
            </Button>
          </div>
        </div>

        {loading && <p className="text-sm text-gray-500">Se încarcă serviciile...</p>}
        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            {categories.length === 0 && !loading && (
              <p className="text-sm text-gray-500">Nu există servicii încă.</p>
            )}

            {categories.map((category, idx) => {
              const isOpen = expanded.has(category.name);
              const categoryColor = categoryColors?.[category.name] || "";
              const cardStyle = categoryColor ? { borderColor: categoryColor } : undefined;
              const headerStyle = categoryColor
                ? { backgroundColor: rgbaFromHex(categoryColor, 0.08), borderColor: categoryColor }
                : undefined;
              return (
                <div
                  key={category.name}
                  className="border rounded-xl bg-white shadow-sm"
                  style={cardStyle}
                  draggable
                  onDragStart={() => setDragCategoryIndex(idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleCategoryDrop(idx)}
                >
                  <div className="flex items-center justify-between p-3 border-b" style={headerStyle}>
                    <div className="flex items-center gap-2">
                      <span className="cursor-move text-gray-400">≡</span>
                      <button
                        onClick={() => toggleCategory(category.name)}
                        className="font-semibold text-sm sm:text-base"
                      >
                        {category.name} ({category.services.length})
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={categoryColor || "#3b82f6"}
                          onChange={async (e) => {
                            try {
                              await setCategoryColor(category.name, e.target.value);
                            } catch (err) {
                              alert(err.message || "Nu s-a putut salva culoarea categoriei");
                            }
                          }}
                          title="Alege culoarea categoriei"
                          className="h-7 w-9 cursor-pointer rounded border border-gray-200 bg-white"
                        />
                        {categoryColor ? (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await setCategoryColor(category.name, null);
                              } catch (err) {
                                alert(err.message || "Nu s-a putut reseta culoarea categoriei");
                              }
                            }}
                            className="text-[11px] text-gray-500 hover:text-gray-700"
                          >
                            Reset
                          </button>
                        ) : (
                          <span className="text-[11px] text-gray-400">Default</span>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openCreateForm(category.name)}
                      >
                        + Serviciu
                      </Button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="p-3 space-y-2">
                      {category.services.map((service, serviceIndex) => (
                        <div
                          key={service.id}
                          className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2 ${
                            service.active === false ? "bg-gray-50 text-gray-400" : "bg-white"
                          }`}
                          draggable
                          onDragStart={() =>
                            setDragService({ category: category.name, index: serviceIndex })
                          }
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => handleServiceDrop(category.name, serviceIndex)}
                        >
                          <div className="flex items-center gap-3">
                            <span className="cursor-move text-gray-400">⋮⋮</span>
                            <div>
                              <p className="text-sm font-semibold">{service.name}</p>
                              <p className="text-xs text-gray-500">
                                {Number(service.basePrice || 0)} RON
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="secondary" onClick={() => openEditForm(service)}>
                              Editează
                            </Button>
                            <Button size="sm" variant="danger" onClick={() => handleDelete(service.id)}>
                              Șterge
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="border rounded-xl p-4 bg-gray-50">
            <h3 className="font-semibold text-sm sm:text-base mb-3">
              {formState.id ? "Editează serviciu" : "Serviciu nou"}
            </h3>

            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500">Nume</label>
                <input
                  className="w-full border rounded p-2 text-sm"
                  value={formState.name}
                  onChange={(e) => handleFormChange("name", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Categorie</label>
                <input
                  className="w-full border rounded p-2 text-sm"
                  value={formState.category}
                  onChange={(e) => handleFormChange("category", e.target.value)}
                  placeholder="Ex: Endo"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs text-gray-500">Preț de bază</label>
                  <input
                    type="number"
                    className="w-full border rounded p-2 text-sm"
                    value={formState.basePrice}
                    onChange={(e) => handleFormChange("basePrice", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Discount %</label>
                  <input
                    type="number"
                    className="w-full border rounded p-2 text-sm"
                    value={formState.defaultDiscount}
                    onChange={(e) => handleFormChange("defaultDiscount", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <p className="text-xs text-gray-500 mb-1">Prețuri pe grup (opțional)</p>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    className="border rounded p-2 text-xs"
                    placeholder="Grup A"
                    value={formState.priceA}
                    onChange={(e) => handleFormChange("priceA", e.target.value)}
                  />
                  <input
                    type="number"
                    className="border rounded p-2 text-xs"
                    placeholder="Grup B"
                    value={formState.priceB}
                    onChange={(e) => handleFormChange("priceB", e.target.value)}
                  />
                  <input
                    type="number"
                    className="border rounded p-2 text-xs"
                    placeholder="Grup C"
                    value={formState.priceC}
                    onChange={(e) => handleFormChange("priceC", e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs text-gray-500">Comision medic</label>
                <select
                  className="w-full border rounded p-2 text-sm"
                  value={formState.commissionTag}
                  onChange={(e) => handleFormChange("commissionTag", e.target.value)}
                >
                  <option value="">Fără comision</option>
                  <option value="primary">Tratament primar</option>
                  <option value="retreat">Retratament</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500">Aplicare</label>
                <select
                  className="w-full border rounded p-2 text-sm"
                  value={formState.scope}
                  onChange={(e) => handleFormChange("scope", e.target.value)}
                >
                  <option value="full_mouth">Globala</option>
                  <option value="arch">Arcada</option>
                  <option value="tooth">Dinte</option>
                </select>
              </div>

              <label className="text-xs flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formState.active}
                  onChange={(e) => handleFormChange("active", e.target.checked)}
                />
                Activ
              </label>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {formState.id ? "Salvează" : "Adaugă"}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setFormState(emptyForm)}
                >
                  Resetează
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
